<template>
	<portal to="panels">
		<helio-panel
			class="scroll--small modules-panel__panel"
			:active="active"
			alignment="right"
			:closable="false"
			close-icon="times"
			close-icon-weight="light"
			:content-scroll="true"
			theme="light"
			:title="modulesText"
			type="overlay"
			@close="$emit('close-modules-panel')"
		>
			<template v-slot:content>
				<div class="modules-panel__wrapper">
					<div class="modules-panel__modules">
						<ul
							v-for="(module, index) in modules"
							:key="index"
							class="modules-panel__module-wrapper"
						>
							<li
								:class="[
									'modules-panel__module',
									{
										'modules-panel__module--opened': openedSubmodule === index,
										'modules-panel__module--active': isTheActiveUrl(module.url),
									}
								]"
							>
								<a
									v-if="!module.submodules"
									:href="!module.submodules && module.url[$i18n.locale]"
									:target="module.target ? module.target : '_self'"
									class="modules-panel__module-name"
									:class="`modules-panel__module-name-${index}`"
								>
									<span>
										<helio-icon
											class="modules-panel__module-icon"
											:token="module.icon"
											weight="light"
										/>

										{{ module.name[$i18n.locale] }}

										<helio-badge
											v-if="module.isNew"
											class="modules-panel__module-badge module-badge-new"
										>
											{{ $t('header.module.new') }}
										</helio-badge>
									</span>
								</a>

								<button
									v-else
									:ref="index"
									:href="!module.submodules && module.url[$i18n.locale]"
									:target="module.target ? module.target : '_self'"
									class="modules-panel__module-name modules-panel__module-name--button"
									:class="`modules-panel__module-name-${index}`"
									@click="toggleSubmodule(index)"
								>
									<span>
										<helio-icon
											class="modules-panel__module-icon"
											:token="module.icon"
											weight="light"
										/>

										{{ module.name[$i18n.locale] }}

										<helio-badge
											v-if="module.isNew"
											class="modules-panel__module-badge module-badge-new"
										>
											{{ $t('header.module.new') }}
										</helio-badge>
									</span>

									<helio-icon
										v-if="module.submodules"
										class="modules-panel__toggle-icon"
										:token="openedSubmodule === index ? 'angle-up' :'angle-down'"
										weight="light"
									/>
								</button>
							</li>

							<Trap :disabled="!showTrap[index]">
								<div
									v-if="module.submodules && showTrap[index]"
									:class="[
										`modules-panel__submodules-wrapper modules-panel__submodules-wrapper-${index}`,
										{
											'modules-panel__submodules-wrapper--opened': openedSubmodule === index,
										}
									]"
									tabIndex="-1"
								>
									<ul
										v-for="(submodule, subindex) in module.submodules"
										ref="list"
										:key="subindex"
										:class="[
											'modules-panel__submodule',
											{
												'modules-panel__submodule--active': isTheActiveUrl(submodule.url, index),
											}
										]"
										tabIndex="-1"
									>
										<li>
											<a
												ref="item"
												class="modules-panel__submodule-name"
												:href="submodule.url[$i18n.locale]"
												:tabindex="tabIndex(index)"
												:target="submodule.target ? submodule.target : '_self'"
											>
												<helio-icon
													class="modules-panel__submodule-icon"
													token="long-arrow-right"
													weight="light"
												/>

												{{ submodule.name[$i18n.locale] }}
											</a>
										</li>
									</ul>
								</div>
							</Trap>
						</ul>
					</div>

					<div
						v-if="links"
						:class="[
							'modules-panel__links',
							{
								'modules-panel__links--opened': linksOpened,
							}
						]"
					>
						<button
							class="modules-panel__links-title"
							@click="toggleLinks"
						>
							<helio-icon
								class="modules-panel__links-icon"
								token="link"
								weight="light"
							/>

							{{ links.title[$i18n.locale] }}

							<helio-icon
								:token="linksOpened ? 'angle-up' :'angle-down'"
								class="modules-panel__toggle-icon"
								weight="light"
							/>
						</button>

						<Trap :disabled="!showLinkTrap">
							<div
								v-if="showLinkTrap"
								:class="[
									'modules-panel__links-wrapper',
									{
										'modules-panel__links-wrapper--opened': linksOpened,
									}
								]"
							>
								<ul
									v-for="(link, index) in links.list"
									:key="index"
									ref="linkList"
									class="modules-panel__link"
								>
									<li>
										<a
											v-if="link.url && link.name"
											ref="linkItem"
											:href="link.url[$i18n.locale]"
											:target="link.target ? link.target : '_self'"
											class="modules-panel__link-name"
										>
											<helio-icon
												class="modules-panel__link-icon"
												token="long-arrow-right"
												weight="light"
											/>

											{{ link.name[$i18n.locale] }}
										</a>
									</li>
								</ul>
							</div>
						</Trap>
					</div>
				</div>
			</template>
		</helio-panel>
	</portal>
</template>

<script>
import Trap from 'vue-focus-lock';
import Icon from '../../../icon/Icon.vue';
import Panel from '../../../panel/Panel.vue';

export default {
	components: {
		Trap,
		'helio-icon': Icon,
		'helio-panel': Panel,
	},

	props: {
		active: {
			type: Boolean,
			required: true,
		},

		modules: {
			type: Array,
			required: true,
		},

		modulesText: {
			type: String,
			required: false,
			default: null,
		},

		links: {
			type: Object,
			required: false,
			default: null,
		},
	},

	data() {
		return {
			openedSubmodule: null,
			linksOpened: false,
			initialOpening: true,
			activeURL: '',
			lastFocusedModule: null,
			indexOfCurrentSubmoduleItem: -1,
			indexOfOpenModule: -1,
			indexOfCurrentListItem: -1,
			showLinkTrap: false,
		};
	},

	computed: {
		numberOfSubmoduleLists() {
			return this.modules.filter(module => module.submodules).length;
		},

		showTrap() {
			return Array.from({ length: this.numberOfSubmoduleLists }, () => false);
		},
	},

	watch: {
		$route() {
			this.setActiveUrl();
		},
	},

	mounted() {
		window.addEventListener('keyup', this.handleKeyup);
		window.addEventListener('keydown', this.checkForTabs);
		this.setActiveUrl();
	},

	beforeDestroy() {
		window.removeEventListener('keyup', this.handleKeyup);
		window.removeEventListener('keydown', this.checkForTabs);
	},

	methods: {
		checkForEscape(event) {
			const { key } = event;

			// if outside trap ESC closes panel
			if (key === 'Escape' && this.showTrap.every(element => element === false)) {
				this.$emit('modules-panel-inactive');
			}

			// SUBMODULES
			// if inside trap ESC closes trap
			if (key === 'Escape' && this.showTrap[this.indexOfOpenModule]) {
				this.closeSubmodules();
				this.$emit('modules-panel-active');
			}

			// LINKS
			// if inside trap ESC closes trap
			if (key === 'Escape' && this.showLinkTrap) {
				this.closeList();
				this.$emit('modules-panel-active');
			}
		},

		checkForArrows(event) {
			const { key } = event;
			let listArray = [];
			let linksArray = [];

			if (this.$refs && this.$refs.item) {
				listArray = [...this.$refs.item];
			}
			if (this.$refs && this.$refs.linkItem) {
				linksArray = [...this.$refs.linkItem];
			}

			// SUBMODULES

			// Up arrow
			if (key === 'ArrowUp' && this.showTrap[this.indexOfOpenModule]) {
				event.preventDefault();
				this.indexOfCurrentSubmoduleItem = this.decrementIndex(
					this.indexOfCurrentSubmoduleItem,
					listArray,
				);
				listArray[this.indexOfCurrentSubmoduleItem].focus();
			}
			// Down arrow
			if (key === 'ArrowDown' && this.showTrap[this.indexOfOpenModule]) {
				event.preventDefault();
				this.indexOfCurrentSubmoduleItem = this.incrementIndex(
					this.indexOfCurrentSubmoduleItem,
					listArray,
				);
				listArray[this.indexOfCurrentSubmoduleItem].focus();
			}

			// LINKS

			// Up arrow
			if (key === 'ArrowUp' && this.showLinkTrap) {
				event.preventDefault();
				this.indexOfCurrentListItem = this.decrementIndex(this.indexOfCurrentListItem, linksArray);
				linksArray[this.indexOfCurrentListItem].focus();
			}
			// Down arrow
			if (key === 'ArrowDown' && this.showLinkTrap) {
				event.preventDefault();
				this.indexOfCurrentListItem = this.incrementIndex(this.indexOfCurrentListItem, linksArray);
				linksArray[this.indexOfCurrentListItem].focus();
			}
		},

		incrementIndex(index, list) {
			if (index < list.length - 1) {
				return index + 1;
			}

			return 0;
		},

		decrementIndex(index, list) {
			if (index > 0) {
				return index - 1;
			}

			return list.length - 1;
		},

		checkForTabs(event) {
			const { key } = event;
			const shiftPressed = event.getModifierState('Shift');

			// Get last focused module
			if (this.showTrap.every(element => element === false)) {
				this.lastFocusedModule = document.activeElement;
				if (this.lastFocusedModule.classList.contains('modules-panel__module-name')) {
					this.lastFocusedModule = this.lastFocusedModule.classList.value.match(/\d+/);
					this.lastFocusedModule = parseInt(this.lastFocusedModule[0], 10);
				}
			}

			// SUBMODULES

			// If a user navigates with shift + tab in the submodule, the parent
			// module will receive focus and the sublist will close
			if ((shiftPressed && key === 'Tab') && this.showTrap[this.indexOfOpenModule]) {
				event.preventDefault();

				// Close submodules
				this.closeSubmodules();

				this.$nextTick(() => {
					// Move focus to last focused module
					const nextFocusedModule = document.getElementsByClassName(`modules-panel__module-name-${this.lastFocusedModule}`)[0];
					nextFocusedModule.focus();
				});
			}

			// If a user navigates with tab in the submodule, the next module
			// will receive focus and the sublist will close
			if (key === 'Tab' && this.showTrap[this.indexOfOpenModule]) {
				event.preventDefault();

				// Close submodules
				this.closeSubmodules();

				this.$nextTick(() => {
					// Is this the last module?
					const isLastModule = this.lastFocusedModule === (this.modules.length - 1);

					// Are there Links in the panel?
					const linksExist = document.getElementsByClassName('modules-panel__links-title').length;

					// Move focus to the next module
					let nextFocusedModule = document.getElementsByClassName(`modules-panel__module-name-${this.lastFocusedModule + 1}`)[0];

					if (isLastModule && linksExist) {
						nextFocusedModule = document.getElementsByClassName('modules-panel__links-title')[0];
					}
					if (isLastModule && !linksExist) {
						nextFocusedModule = document.getElementsByClassName('modules-panel__module-name-0')[0];
					}

					nextFocusedModule.focus();
				});
			}

			// LINKS

			// If a user navigates with shift + tab in the link list, the parent
			// module will receive focus and the sublist will close
			if ((shiftPressed && key === 'Tab') && this.showLinkTrap) {
				event.preventDefault();

				// Close list
				this.closeList();

				// Move focus to last focused module
				const nextFocusedModule = document.getElementsByClassName('modules-panel__links-title')[0];
				nextFocusedModule.focus();
			}

			// If a user navigates with tab in the link list, the next module
			// will receive focus and the sublist will close
			if (key === 'Tab' && this.showLinkTrap) {
				event.preventDefault();

				// Close list
				this.closeList();

				// Move focus to the next module
				const nextFocusedModule = document.getElementsByClassName('modules-panel__module-name-0')[0];
				nextFocusedModule.focus();
			}
		},

		closeList() {
			this.showLinkTrap = false;
			this.indexOfCurrentListItem = -1;
			this.openedSubmodule = null;
			this.linksOpened = false;
		},

		closeSubmodules() {
			this.showTrap[this.indexOfOpenModule] = false;
			this.indexOfCurrentSubmoduleItem = -1;
			this.indexOfOpenModule = -1;
			this.openedSubmodule = null;
			this.linksOpened = false;
		},

		handleKeyup(event) {
			this.checkForArrows(event);
			this.checkForEscape(event);
		},

		tabIndex(index) {
			return this.showTrap[index] ? 0 : -1;
		},

		toggleSubmodule(index) {
			if (this.modules && this.modules[index].submodules) {
				this.showTrap[index] = !this.showTrap[index];
				this.indexOfOpenModule = this.showTrap[index] ? index : -1;
				this.openedSubmodule = this.openedSubmodule !== index ? index : null;
				this.linksOpened = false;
			}
		},

		toggleLinks() {
			this.linksOpened = !this.linksOpened;
			this.showLinkTrap = !this.showLinkTrap;
			this.openedSubmodule = null;
		},

		setActiveUrl() {
			const url = window.location.href;
			const queryIndex = url.indexOf('?');
			const urlWithoutParams = queryIndex > 0 ? url.substring(0, queryIndex) : url;
			this.activeURL = this.cleanUrl(urlWithoutParams);
		},

		isTheActiveUrl(url, index = null) {
			if (this.$i18n) {
				const match = this.activeURL === this.cleanUrl(url[this.$i18n.locale]);

				if (this.initialOpening && match && index) {
					this.openedSubmodule = index;
					this.initialOpening = false;
				}

				return match;
			}

			return false;
		},
	},
};
</script>

<style src="./modules-panel.scss" lang="scss" />
